import { useMemo, type FC } from 'react';
import { DataTable } from './data-table';
import type { DataTableColumn } from './data-table-features';
import type { DataType } from './data-types';

export interface DataPreviewColumn {
  name: string;
  type: DataType;
  /** Tints the column: `added` in brand, `retyped` in amber. */
  change?: 'added' | 'retyped' | null;
}

interface DataPreviewTableProps {
  columns: readonly DataPreviewColumn[];
  /** One string per column, in column order. */
  rows: readonly (readonly string[])[];
  /** Names the table for assistive tech, e.g. "Data at Step 2 · Aggregate". */
  label: string;
  className?: string;
}

type PreviewRow = string[];

/**
 * Sample rows under typed column headers: a `DataTable` set up for raw
 * field values (monospace, sortable). Changed columns are tinted so a
 * step's effect reads at a glance; with no rows, only the schema shows.
 */
export const DataPreviewTable: FC<DataPreviewTableProps> = ({
  columns,
  rows,
  label,
  className
}) => {
  const tableColumns = useMemo<DataTableColumn<PreviewRow>[]>(
    () =>
      columns.map((column, index) => ({
        id: column.name,
        header: column.name,
        accessorFn: (row: PreviewRow) => row[index],
        sortFn: 'alphanumeric',
        meta: { type: column.type, change: column.change, mono: true }
      })),
    [columns]
  );
  const data = useMemo(() => rows.map((row) => [...row]), [rows]);
  return (
    <DataTable
      label={label}
      columns={tableColumns}
      data={data}
      emptyMessage="No sample rows; showing the schema only."
      className={className}
    />
  );
};
